import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { triggerBrowserDownload } from 'services/http/http';
import { logError } from 'services/logging/logging';
import { FileRow } from 'components/Form/FileRow/FileRow';
import { useSchemableFormFileUploadContext } from 'components/SchemableForm/contexts/SchemableFormFileUploadContext/SchemableFormFileUploadContext';

import { FileUploadValue } from '../types';

type FileUploadFieldViewProps = {
  field: ui.FileUploadField;
  value: FileUploadValue;
};

export const FileUploadFieldView = ({ field, value }: FileUploadFieldViewProps) => {
  const { t } = useTranslation();
  const fileUploadContextValue = useSchemableFormFileUploadContext();
  const { getFileDownloadUrl } = fileUploadContextValue || {};
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(
    async (file: NonNullable<FileUploadValue>) => {
      if (!getFileDownloadUrl) {
        return;
      }
      setIsDownloading(true);
      try {
        const { url } = await getFileDownloadUrl(file.file_name);
        triggerBrowserDownload(url, file.original_name || file.file_name);
      } catch (error) {
        logError(error);
      } finally {
        setIsDownloading(false);
      }
    },
    [getFileDownloadUrl],
  );

  return (
    <FileRow
      label={field.label}
      icon={DocumentTextIcon}
      text={value?.original_name ?? ''}
      action={
        value
          ? {
              label: t('common:download'),
              onClick: () => handleDownload(value),
              loading: isDownloading,
              LeftIcon: ArrowDownTrayIcon,
              analyticsId: 'file_row_download',
            }
          : undefined
      }
    />
  );
};
